import React, { useEffect, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { Location } from 'history';

import Modal from '@rover/components/src/OverflowTextModal/Modal';
import Report from '@rover/icons/svg/report.svg';
import { Box, Button, Flex, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import emitMetric from '@rover/utilities/emitMetric';
import { redirect } from '@rover/utilities/url';

import ReportProfileForm from './ReportProfileForm';

type Props = {
  isLoggedIn?: boolean;
  location: Location;
  onSuccess?: (desc: string) => void;
};

const OPEN_MODAL_FLAG_HASH = 'flagDialog';

const ReportProfileSection = ({ isLoggedIn, location, onSuccess }: Props): JSX.Element => {
  const { t } = useLingui();
  const theme = useTheme();
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

  useEffect(() => {
    if (isLoggedIn && location.hash === `#${OPEN_MODAL_FLAG_HASH}`) {
      setModalIsOpen(true);
    }
  }, []);

  const handleClick = (e?: React.MouseEvent<HTMLButtonElement>): void => {
    e?.preventDefault();
    emitMetric('sitter_profile_report_profile_started');
    Logger.info('sitter_profile_report_profile_started');
    if (!isLoggedIn) {
      redirect(`/account/continue/?next=${location.pathname}%23${OPEN_MODAL_FLAG_HASH}`);
      return;
    }
    setModalIsOpen(true);
  };

  const handleModalClose = (): void => {
    setModalIsOpen(false);
  };

  const handleOnSuccess = (): void => {
    emitMetric('sitter_profile_report_profile_submitted');
    Logger.info('sitter_profile_report_profile_submitted');
    setModalIsOpen(false);
    if (onSuccess) onSuccess(t`Thank you for flagging this. We'll look at it as soon as possible`);
  };

  return (
    <Flex flexDirection="column">
      <Flex alignItems="center" gap="2x" data-testid="report-profile">
        <Report width="16px" height="16px" fill={theme.colors.interactive_link.secondary_base} />
        <Button
          variant="flat"
          onClick={handleClick}
          m="0x"
          p="0x"
          sx={{
            color: theme.colors.interactive_link.secondary_base,
            textDecorationLine: 'underline',
            '&:hover': {
              textDecorationLine: 'underline',
              color: theme.colors.interactive_link.secondary_base,
              background: 'none',
            },
            '&:focus': {
              textDecorationLine: 'underline',
              color: theme.colors.interactive_link.secondary_base,
              background: 'none',
            },
          }}
          data-testid="report-profile-button"
        >
          <Text sx={{ fontWeight: '600' }}>{t`Report this profile`}</Text>
        </Button>

        <Modal hasCloseButton={false} onClose={handleModalClose} isOpen={modalIsOpen}>
          <Box width="100%" px="1x" data-testid="report-profile-modal">
            <ReportProfileForm onSuccessReport={handleOnSuccess} onClose={handleModalClose} />
          </Box>
        </Modal>
      </Flex>
    </Flex>
  );
};

export default ReportProfileSection;
